import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [fullName, setFullName] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check profile name using React Query
  const { isLoading: profileLoading } = useQuery({
    queryKey: ["profile-name", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        setShowNamePrompt(true);
        return null;
      }

      if (!profile?.full_name) {
        setShowNamePrompt(true);
      }
      return profile;
    },
    enabled: !!user,
  });

  // Redirect if not authenticated (after all hooks)
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth");
    }
  }, [isLoading, user, navigate]);

  if (isLoading || profileLoading || (!isLoading && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie Ihren Namen ein",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Fehler",
        description: "Benutzer nicht gefunden",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error: insertError } = await supabase
        .from("profiles")
        .insert([{ id: user.id, full_name: fullName.trim() }]);

      if (insertError) {
        console.error("Error inserting profile, trying update:", insertError);
        const { error: updateError } = await supabase
          .from("profiles")
          .update({ full_name: fullName.trim() })
          .eq("id", user.id);

        if (updateError) {
          console.error("Error updating profile:", updateError);
          throw updateError;
        }
      }

      setShowNamePrompt(false);
      toast({
        title: "Erfolg",
        description: "Ihr Name wurde gespeichert",
      });
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Fehler",
        description: "Der Name konnte nicht gespeichert werden",
        variant: "destructive",
      });
    }
  };

  if (showNamePrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-8 p-8 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <h2 className="text-2xl font-bold">Willkommen!</h2>
            <p className="mt-2 text-gray-600">
              Bitte geben Sie Ihren vollständigen Namen ein, um fortzufahren
            </p>
          </div>
          <form onSubmit={handleNameSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <Input
                type="text"
                placeholder="Vollständiger Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
              <Button type="submit" className="w-full">
                Speichern
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
